/**
 * Headless tile renderer page
 * Used by scripts to render individual terrain tiles via Puppeteer
 */

import { BlueprintRegistry } from '@/qud/ecs/BlueprintRegistry';
import { registerTerrainParts } from '@/qud/ecs/parts';
import { WebGLContext } from '@/graphics/WebGLContext';
import { TileMapShader } from '@/graphics/TileMapShader';
import { TileMapRenderer } from '@/graphics/TileMapRenderer';
import { QudSpriteLoader } from '@/qud/map/QudSpriteLoader';
import type { Tile } from '@/qud/types';
import { getColor, hexToRgb } from '@/qud/sprites/qudColors';
import { CELL_WIDTH, CELL_HEIGHT } from '@/qud/sprites/constants';

export async function renderHeadlessTilePage(appElement: HTMLDivElement): Promise<void> {
  // Parse query params from URL: /headless-render?blueprint=TerrainEynRoj&scale=2
  const params = new URLSearchParams(window.location.search);
  const blueprintName = params.get('blueprint') || 'TerrainEynRoj';
  const scale = parseInt(params.get('scale') || '1', 10);
  const cropHeight = parseInt(params.get('cropHeight') || '16', 10);

  // Canvas size scales with the scale parameter
  const canvasWidth = 16 * scale;
  const canvasHeight = 24 * scale;
  const scaledCropHeight = cropHeight * scale;

  // Container must match canvas size so WebGLRenderer.setupCanvas doesn't resize
  appElement.innerHTML = `
    <div style="padding: 20px;">
      <h2>Headless Render: ${blueprintName} (${scale}x)</h2>
      <div id="render-container" style="width: ${canvasWidth}px; height: ${canvasHeight}px;">
        <canvas id="render-canvas" style="width: ${canvasWidth}px; height: ${canvasHeight}px;"></canvas>
      </div>
      <div id="status">Loading...</div>
      <div id="crop-height" data-value="${scaledCropHeight}"></div>
    </div>
  `;

  const canvas = document.getElementById('render-canvas') as HTMLCanvasElement;
  const statusEl = document.getElementById('status') as HTMLDivElement;

  try {
    // Load blueprints
    const blueprintsResponse = await fetch('/data/blueprints.json');
    const blueprintsData = await blueprintsResponse.json();

    const blueprintRegistry = new BlueprintRegistry();
    registerTerrainParts(blueprintRegistry.partRegistry);
    blueprintRegistry.loadFromData(blueprintsData);

    // Create game object from blueprint
    const gameObject = blueprintRegistry.createObject(blueprintName);

    // Get render info from the Render part
    const renderPart = gameObject.getPart('Render') as {
      tile: string;
      tileColor: string;
      detailColor: string;
    } | null;
    if (!renderPart) {
      throw new Error(`No Render part on ${blueprintName}`);
    }

    const tilePath = renderPart.tile;
    const tileColor = renderPart.tileColor || 'y';
    const detailColor = renderPart.detailColor || 'y';

    // Normalize tile path to sprite name
    const spriteName = tilePath.toLowerCase().replace(/[/\\]/g, '_');

    // Get colors as normalized RGB
    const fgHex = getColor(tileColor);
    const detailHex = getColor(detailColor);
    const fgRgb = hexToRgb(fgHex);
    const detailRgb = hexToRgb(detailHex);

    // Create tile data
    const tile: Tile = {
      blueprints: [blueprintName],
      sprite: spriteName,
      renderColors: {
        fg: [fgRgb.r / 255, fgRgb.g / 255, fgRgb.b / 255],
        detail: [detailRgb.r / 255, detailRgb.g / 255, detailRgb.b / 255],
      },
    };

    // Create renderer
    const ctx = new WebGLContext(canvas);
    const shader = new TileMapShader(ctx.gl);
    const renderer = new TileMapRenderer(ctx, shader, CELL_WIDTH, CELL_HEIGHT);
    ctx.setDimensions(canvasWidth, canvasHeight);
    renderer.setView(0, 0, scale);

    // Initialize sprite loader and load the sprite
    const spriteLoader = new QudSpriteLoader();
    await spriteLoader.initialize();
    spriteLoader.loadSprite(spriteName, renderer.getAtlasAllocator());

    // Get cell index for sprite
    const cellIndex = spriteLoader.getCell(spriteName);
    if (cellIndex === undefined) {
      throw new Error(`Failed to load sprite: ${spriteName}`);
    }

    // Render single tile
    const fg = tile.renderColors!.fg;
    const detail = tile.renderColors!.detail;

    // Clear canvas at start of frame (before any renderers draw)
    ctx.bindDefaultFramebuffer();
    ctx.clear(0.1, 0.1, 0.1, 1.0);

    renderer.setVisibleTileRange(0, 0, 1, 1);
    renderer.beginFrame();
    renderer.addInstance(
      0,
      0,
      cellIndex,
      fg[0],
      fg[1],
      fg[2],
      1.0,
      detail[0],
      detail[1],
      detail[2],
      1.0
    );
    renderer.endFrame();

    statusEl.textContent = `Rendered: ${blueprintName} (sprite: ${spriteName})`;

    // Signal to Playwright that we're done
    // @ts-expect-error window global
    window.__HEADLESS_RENDER_COMPLETE__ = true;
    // @ts-expect-error window global
    window.__HEADLESS_CANVAS__ = canvas;
    // @ts-expect-error window global
    window.__HEADLESS_CROP_HEIGHT__ = scaledCropHeight;
  } catch (error) {
    statusEl.textContent = `Error: ${error instanceof Error ? error.message : String(error)}`;
    console.error(error);
    // @ts-expect-error window global
    window.__HEADLESS_RENDER_ERROR__ = error;
  }
}
